import React, { Fragment, MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import ReCAPTCHA from 'react-google-recaptcha';

import Input from '@src/components/ui2/Input';
import { authSelector, buyerIsForeign, errorsSelector, sendingFormSelector } from '@src/features/customers';
import { useSignup } from '@src/features/customers/auth/signup';
import { SIGNUP } from '@src/features/customers/auth/legacy/AuthDisplay';
import { appInfoSelector } from '@src/stores/app';
import { Address, UserRequest } from '@src/core/buyerApiClient';
import { Controller, FieldValues, useForm, UseFormSetError } from 'react-hook-form';
import { MdArrowForward } from 'react-icons/md';
import Button from '@src/components/ui2/Button';
import { schema } from './schema';
import { CheckboxWrapper, IdentityTypeSelector, RecaptchaContainer, TermsMessage } from './styles';
import PhoneInput from '@src/components/ui2/PhoneInput';
import { Terms } from '@src/features/terms';
import ResponsiveModal from '@src/components/ui2/ResponsiveModal';
import { convertFieldContextErrorToHookForm, getErrorMessage } from '@src/common';
import { HelperText } from '@src/components/ui2/Input/styles';
import Radio from '@src/components/ui2/Radio';
import Checkbox from '@src/components/ui2/Checkbox';
import NationalitySelect from '@src/components/ui2/NationalitySelect';
import { CountryCode } from '@src/common/auth';
import countries from '@src/data/countries.json';
import { getDefaultValues, ForeignIdentityType, IdentityType, createPayload } from './utils';
import FeatureFlag from '@src/components/FeatureFlag';
import {
  resolveForeignIdentityType,
  resolveDialCode,
  isCnpjForeignConflict,
  isBuyerForeign,
} from '@src/domains/customers';

type UserFormProps = {
  fillData?: boolean;
};

const UserForm = (props: UserFormProps) => {
  const { fillData = false } = props;

  const { i18n, i18nElement } = useTranslate();
  const { createAccount, updateAccount } = useSignup();

  const { buyer } = useAppSelector(authSelector);
  const sendingForm = useAppSelector(sendingFormSelector);
  const contextErrors = useAppSelector(errorsSelector);
  const isForeignBuyer = useAppSelector(buyerIsForeign);

  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyPolicyModalOpen, setPrivacyPolicyModalOpen] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null | undefined>(undefined);
  const [address, setAddress] = useState<Address | undefined>();

  const appInfo = useAppSelector(appInfoSelector);

  const isEdit = !!buyer?.id;

  const defaultValues = useMemo(() => {
    const iAmForeign = isForeignBuyer && !!appInfo?.allowForeignBuyers;
    return getDefaultValues(buyer, fillData, iAmForeign);
  }, [buyer, fillData]);

  const { handleSubmit, control, formState, setError, watch, setValue } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: defaultValues,
    resolver: zodResolver(schema(i18n, isEdit)),
  });
  const { errors, isSubmitting } = formState;

  const saveUser = (payload: FieldValues) => {
    const { createParams, editParams } = createPayload(payload, recaptchaToken);

    if (isEdit) {
      updateAccount(editParams);
    } else {
      createAccount(createParams);
    }
  };

  const iAmForeign = watch('iAmForeign');
  const nationality = watch('countryCode');
  const identityType = watch('identityType');
  const isIdentityTypeCPF = identityType === IdentityType.CPF;
  const isIdentityTypeCNPJ = identityType === IdentityType.CNPJ;

  const showPassportOption = useMemo(() => isBuyerForeign(nationality), [nationality]);
  const showNationalDocumentOption = useMemo(
    () => resolveForeignIdentityType(nationality) === ForeignIdentityType.FOREIGN_ID,
    [nationality],
  );

  const handleChangeNationality = (alpha3: string) => {
    setValue('countryCode', alpha3);
    setValue('foreignIdentityType', resolveForeignIdentityType(alpha3));
    const dialCode = resolveDialCode(alpha3);
    if (dialCode) setValue('phone', `+${dialCode}`);
  };

  const handleChangeIamForeign = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    if (checked) {
      const country = countries.find((c) => c.alpha3 === nationality);

      setValue('phone', `+${country!.dialCode!}`);
    } else {
      setValue('phone', '+55');
      setValue('countryCode', CountryCode.BRAZIL);
    }
  };

  useEffect(() => {
    if (isCnpjForeignConflict(identityType, iAmForeign)) {
      setValue('identity', '');
      setValue('iAmForeign', false);
    }
  }, [identityType, iAmForeign]);

  useEffect(() => {
    const contexts = ['signup', 'updateUser'];

    for (const context of contexts) {
      for (const field of Object.keys(defaultValues)) {
        convertFieldContextErrorToHookForm<UserRequest>(
          field as keyof UserRequest,
          setError as UseFormSetError<UserRequest>,
          contextErrors,
          context,
        );
      }
    }
  }, [contextErrors]);

  useEffect(() => {
    if (!iAmForeign && address) {
      setAddress(undefined);
    }
  }, [iAmForeign]);

  return (
    <form onSubmit={handleSubmit(saveUser)} noValidate>
      <Controller
        name='identityType'
        control={control}
        render={({ field: { name, value, onChange } }) => (
          <IdentityTypeSelector>
            <Radio
              name={name}
              value={IdentityType.CPF}
              label={i18n('direct_link_user_form_identity_type_cpf')}
              checked={value === IdentityType.CPF}
              onChange={onChange}
            />
            <Radio
              name={name}
              value={IdentityType.CNPJ}
              label={i18n('direct_link_user_form_identity_type_cnpj')}
              checked={value === IdentityType.CNPJ}
              onChange={onChange}
            />
          </IdentityTypeSelector>
        )}
      />

      <Controller
        name='email'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            type='email'
            label={i18n('direct_link_user_form_email_label')}
            name={name}
            value={value}
            required
            onChange={onChange}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            disabled={sendingForm}
            {...rest}
          />
        )}
      />

      {!isEdit ? (
        <Controller
          name='emailConfirmation'
          control={control}
          render={({ field: { onChange, value, name, ...rest } }) => (
            <Input
              type='email'
              label={i18n('direct_link_user_form_email_confirmation_label')}
              name={name}
              value={value}
              required
              onChange={onChange}
              error={!!errors[name]}
              helperText={errors[name]?.message as string}
              disabled={sendingForm}
              {...rest}
            />
          )}
        />
      ) : null}

      {isIdentityTypeCNPJ ? (
        <>
          <Controller
            name='corporateName'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='text'
                label={i18n('direct_link_user_form_corporate_name')}
                name={name}
                value={value}
                required
                onChange={onChange}
                error={!!errors[name]}
                helperText={errors[name]?.message as string}
                disabled={sendingForm}
                {...rest}
              />
            )}
          />

          <Controller
            name='identity'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='text'
                label={i18n('direct_link_user_form_cnpj_identity_label')}
                mask='cnpj'
                name={name}
                value={value}
                required
                onChange={onChange}
                error={!!errors[name]}
                helperText={errors[name]?.message as string}
                disabled={sendingForm}
                {...rest}
              />
            )}
          />
        </>
      ) : null}

      <Controller
        name='firstName'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            type='text'
            label={i18n(
              isIdentityTypeCPF
                ? 'direct_link_user_form_name_label'
                : 'direct_link_user_form_representative_legal_name_label',
            )}
            name={name}
            value={value}
            required
            onChange={onChange}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            disabled={sendingForm}
            {...rest}
          />
        )}
      />

      <Controller
        name='lastName'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            type='text'
            label={i18n(
              isIdentityTypeCPF
                ? 'direct_link_user_form_lastname_label'
                : 'direct_link_user_form_representative_legal_lastname_label',
            )}
            name={name}
            value={value}
            required
            onChange={onChange}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            disabled={sendingForm}
            {...rest}
          />
        )}
      />

      <FeatureFlag
        feature='PR-2195-collect-buyer-cep'
        active={() => (
          <Controller
            name='zipCode'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='text'
                label={i18n('direct_link_user_form_zip_code_label')}
                mask={iAmForeign ? undefined : 'cep'}
                name={name}
                value={value}
                onChange={onChange}
                error={!!errors[name]}
                helperText={errors[name]?.message as string}
                {...rest}
                // onBlur={(e) => (iAmForeign ? undefined : handleZipCodeBlur(e))}
                disabled={sendingForm}
              />
            )}
          />
        )}
      />

      <FeatureFlag
        feature='PR-2195-collect-buyer-birthdate'
        active={() => (
          <Controller
            name='birthdate'
            control={control}
            render={({ field: { name, ...rest } }) => (
              <Input // replace with DatePicker from uni
                label={i18n('direct_link_user_form_birthday_label')}
                pattern='^([0-2][0-9]|30|31)/(0[0-9]|10|11|12)/[0-9]{4}$'
                placeholder='DD/MM/YYYY'
                mask='birthdate'
                name={name}
                error={!!errors[name]}
                helperText={errors[name]?.message as string}
                disabled={sendingForm}
                {...rest}
              />
            )}
          />
        )}
      />

      {isIdentityTypeCPF && appInfo?.allowForeignBuyers ? (
        <Controller
          name='iAmForeign'
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <CheckboxWrapper>
              <Checkbox
                name={name}
                accentColor='#A5763D'
                label={i18n('direct_link_user_form_is_foreign')}
                checked={value}
                onChange={(e) => {
                  onChange(e);
                  handleChangeIamForeign(e);
                }}
              />
            </CheckboxWrapper>
          )}
        />
      ) : null}

      {iAmForeign ? (
        <Fragment>
          <Controller
            name='countryCode'
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <div style={{ margin: '0px 0px 16px' }}>
                <NationalitySelect
                  name={name}
                  label={i18n('direct_link_user_form_nationality')}
                  onChange={(event) => {
                    onChange(event);
                    handleChangeNationality(event.target.value);
                  }}
                  value={value ?? undefined}
                />
              </div>
            )}
          />

          <Controller
            name='identity'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='text'
                label={i18n('direct_link_user_form_document')}
                name={name}
                value={value}
                required
                maxLength={255}
                onChange={onChange}
                error={!!errors[name]}
                helperText={errors[name]?.message as string}
                disabled={sendingForm}
                {...rest}
              />
            )}
          />

          <Controller
            name='foreignIdentityType'
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <IdentityTypeSelector style={{ marginTop: '16px' }}>
                {showNationalDocumentOption ? (
                  <Radio
                    name={name}
                    onChange={onChange}
                    value={ForeignIdentityType.FOREIGN_ID}
                    label={i18n('direct_link_user_form_identity_national_document')}
                    checked={value === ForeignIdentityType.FOREIGN_ID}
                  />
                ) : null}

                {showPassportOption ? (
                  <Radio
                    name={name}
                    onChange={onChange}
                    value={ForeignIdentityType.PASSPORT}
                    label={i18n('direct_link_user_form_identity_passport')}
                    checked={value === ForeignIdentityType.PASSPORT}
                  />
                ) : null}
              </IdentityTypeSelector>
            )}
          />
        </Fragment>
      ) : null}

      {!iAmForeign && isIdentityTypeCPF ? (
        <Controller
          name='identity'
          control={control}
          render={({ field: { onChange, value, name, ...rest } }) => (
            <Input
              type='text'
              label={i18n('direct_link_user_form_cpf_identity_label')}
              mask='cpf'
              name={name}
              value={value}
              required
              onChange={onChange}
              error={!!errors[name]}
              helperText={errors[name]?.message as string}
              disabled={sendingForm}
              {...rest}
            />
          )}
        />
      ) : null}

      <Controller
        name='phone'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <PhoneInput
            label={i18n('direct_link_user_form_phone_label')}
            name={name}
            value={value}
            required
            onChange={onChange}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            disabled={sendingForm}
            {...rest}
          />
        )}
      />

      {appInfo?.recaptchaKey && !isEdit && (
        <RecaptchaContainer>
          <ReCAPTCHA sitekey={appInfo.recaptchaKey} onChange={setRecaptchaToken} aria-disabled={sendingForm} />
          {getErrorMessage(contextErrors[SIGNUP], 'recaptchaToken') ? (
            <HelperText error>{i18n('recaptcha_validation_error')}</HelperText>
          ) : null}
        </RecaptchaContainer>
      )}

      <TermsMessage>
        {i18nElement('direct_link_user_form_terms_message')}
        <strong>
          <a href='#' onClick={() => setTermsModalOpen(true)}>
            {i18nElement('direct_link_user_form_terms_message_use_terms')}
          </a>
        </strong>
        {i18nElement('direct_link_user_form_terms_message_and')}
        <strong>
          <a href='#' onClick={() => setPrivacyPolicyModalOpen(true)}>
            {i18nElement('direct_link_user_form_terms_message_privacy_policy')}
          </a>
        </strong>
      </TermsMessage>

      <ResponsiveModal
        title={i18n('direct_link_user_form_terms_modal_title')}
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      >
        <Terms type='terms_of_use' />
      </ResponsiveModal>

      <ResponsiveModal
        title={i18n('direct_link_user_form_privacy_policy_modal_title')}
        open={privacyPolicyModalOpen}
        onClose={() => setPrivacyPolicyModalOpen(false)}
      >
        <Terms type='privacy_policy' />
      </ResponsiveModal>

      <Button fullWidth type='submit' loading={sendingForm || isSubmitting}>
        {i18n('direct_link_user_form_continue')} <MdArrowForward />
      </Button>
    </form>
  );
};

export default UserForm;
